import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import Head from 'expo-router/head';
import { Printer, Download } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../../src/theme';
import { Button, LoadingSpinner, EmptyState } from '@components/common';
import { Header } from '@components/layout';
import { StaticPropertyMap } from '@components/report/StaticPropertyMap';
import { BeforeAfterFigure, SinglePhotoFigure, byline } from '@components/report/EvidenceFigure';
import { useChecklistStore } from '../../../src/store/checklistStore';
import { useStructuresStore } from '../../../src/store/structuresStore';
import { usePostsStore } from '../../../src/store/postsStore';
import { useAuthStore } from '../../../src/store/authStore';
import { checklistsApi } from '../../../src/api/checklists';
import { formatShortDate } from '../../../src/utils/time';
import {
  buildReportModel,
  RUNG_LABEL,
  type ReportModel,
  type ReportRow,
} from '../../../src/utils/reportModel';
import { isPdfDownloadSupported, downloadReportPdf } from '../../../src/utils/reportPdf';
import type { InsuranceChecklist, Structure, StructureClaim } from '../../../src/types';

// Hides the app shell and the page's own controls when the record goes to
// paper, and keeps a row or a section from being split across a page break.
// Injected here rather than in app/+html.tsx because it is only wanted here.
const PRINT_CSS = `
@media print {
  @page { margin: 14mm; }
  body { background: #fff !important; }
  [data-print-hide] { display: none !important; }
  [data-print-break] { break-before: page; page-break-before: always; }
  [data-print-keep] { break-inside: avoid; page-break-inside: avoid; }
  /* The record renders inside a ScrollView: a ~700px box holding ~4800px of
     content. Printing a fixed-height scroller lays it out at that height and
     clips the rest, so the whole record came out as one page with the content
     sized to the box rather than the paper. Every shell wrapper above it fixes
     a height too, hence the stamped ancestor chain. Height and overflow only:
     adding flex:none here ballooned the record to 30 pages. */
  html, body { height: auto !important; overflow: visible !important; }
  [data-print-flow] {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }
  /* Colors are inline per element and come from the active theme, so a record
     printed from dark mode put near-white ink on the white background forced
     above and came out looking blank. Repaint it in the light theme's own
     values (src/theme/colors.ts) so the paper reads the same whichever theme
     the screen was in. The rung keeps its meaning rather than being flattened
     with everything else. Surfaces go transparent: a dark surface would
     otherwise print as a solid block. */
  [data-print-flow] * {
    color: #161615 !important;
    background-color: transparent !important;
    border-color: #ececea !important;
  }
  [data-print-rung='documented'] { color: #2d6a4f !important; }
  [data-print-rung='open'] { color: #9d9d99 !important; }
}
`;

// react-native-web drops unknown props on View, so a raw data-* attribute
// never reaches the DOM and every rule above matches nothing. dataSet is
// RNW's sanctioned escape hatch. Not in React Native's prop types.
const mark = (key: 'printHide' | 'printBreak' | 'printKeep' | 'printFlow') =>
  ({ dataSet: { [key]: 'true' } }) as object;

// Same escape hatch, carrying a value: print repaints every colour, so the
// rung has to say which of its two it should keep.
const markRung = (documented: boolean) =>
  ({ dataSet: { printRung: documented ? 'documented' : 'open' } }) as object;

function formatGeneratedAt(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function Row({ row }: { row: ReportRow }) {
  const { colors } = useTheme();
  const documented = row.rung === 'pair' || row.rung === 'photo' || row.rung === 'note';
  // The rung is the honest claim, so it carries the only color in the row.
  const rungColor = documented ? colors.progress : colors.textTertiary;
  // Anything a pair already shows, either half of it, must not appear again
  // on its own below.
  const loosePhotos = row.posts.filter((p) => !row.consumedByPair.has(p.id) && p.imageUrl);
  const notes = row.posts.filter((p) => !row.consumedByPair.has(p.id) && !p.imageUrl && p.caption);

  return (
    <View
      testID={`report-row-${row.key}`}
      style={[styles.row, { borderTopColor: colors.border }]}
      {...mark('printKeep')}>
      <View style={styles.rowHead}>
        <Text style={[styles.rowLabel, { color: colors.text }]}>{row.label}</Text>
        <Text
          testID={`report-rung-${row.key}`}
          {...markRung(documented)}
          style={[styles.rung, { color: rungColor }]}>
          {RUNG_LABEL[row.rung]}
        </Text>
      </View>
      {row.description ? (
        <Text style={[styles.rowBody, { color: colors.textSecondary }]}>{row.description}</Text>
      ) : null}
      {row.pairs.map((pair) => (
        <BeforeAfterFigure key={pair.post.id} pair={pair} testID={`report-pair-${pair.post.id}`} />
      ))}
      {loosePhotos.map((post) => (
        <SinglePhotoFigure key={post.id} post={post} />
      ))}
      {notes.map((post) => (
        <View key={post.id} style={styles.note}>
          <Text style={[styles.rowBody, { color: colors.text }]}>{post.caption}</Text>
          <Text style={[styles.byline, { color: colors.textTertiary }]}>{byline(post)}</Text>
        </View>
      ))}
    </View>
  );
}

/**
 * The printable property readiness record.
 *
 * Deliberately about the PROPERTY, not about a checklist: rows are shared
 * topics rather than one model's items, so the same document serves every
 * insurer and the cross-model page below can exist at all. See
 * src/utils/reportModel.ts for the coverage rule and the evidence ladder.
 */
export default function StructureReportScreen() {
  const { id: structureId } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);

  const rootRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const el = document.createElement('style');
    el.textContent = PRINT_CSS;
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);

  const {
    selectedChecklist,
    structureProgress,
    fetchChecklistForStructure,
    fetchStructureProgress,
  } = useChecklistStore();
  const { fetchStructure, getClaimForStructure } = useStructuresStore();
  const { fetchStructureEvidencePosts } = usePostsStore();

  const [structure, setStructure] = useState<Structure | null>(null);
  const [claim, setClaim] = useState<StructureClaim | null>(null);
  const [allChecklists, setAllChecklists] = useState<InsuranceChecklist[]>([]);
  const [posts, setPosts] = useState<Awaited<ReturnType<typeof fetchStructureEvidencePosts>>>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Marking the ScrollView is not enough on its own: the app shell wrappers
  // between it and <body> each fix a height, and any one of them still clips
  // the record to a single page. They belong to the shell, not to this
  // screen, so stamp the live chain and take the marks back off on unmount.
  // Keyed on isLoading because rootRef only attaches once the record renders;
  // on mount this screen is still showing the spinner.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const stamped: HTMLElement[] = [];
    for (
      let n = rootRef.current as unknown as HTMLElement | null;
      n && n !== document.documentElement;
      n = n.parentElement
    ) {
      n.dataset.printFlow = 'true';
      stamped.push(n);
    }
    return () => stamped.forEach((n) => delete n.dataset.printFlow);
  }, [isLoading]);

  useEffect(() => {
    if (!user) router.replace('/(tabs)');
  }, [user]);

  useEffect(() => {
    const load = async () => {
      if (!structureId) return;
      setIsLoading(true);
      try {
        const s = await fetchStructure(structureId);
        setStructure(s);
        const c = await getClaimForStructure(structureId);
        setClaim(c);
        const checklist = await fetchChecklistForStructure(structureId, c);
        if (checklist) await fetchStructureProgress(structureId, checklist.id);
        // The cross-model page needs every model, including ones this owner
        // has never opened; those carry no progress doc, which is why
        // coverage counts evidence rather than ticks.
        const [models, evidence] = await Promise.all([
          checklistsApi.getChecklists().catch(() => []),
          fetchStructureEvidencePosts(structureId, { force: true }).catch(() => []),
        ]);
        setAllChecklists(models);
        setPosts(evidence);
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store actions are stable
  }, [structureId]);

  const report: ReportModel | null = useMemo(() => {
    if (!structure) return null;
    return buildReportModel({
      structure,
      claim,
      checklist: selectedChecklist,
      progress: structureProgress,
      allChecklists,
      posts,
    });
  }, [structure, claim, selectedChecklist, structureProgress, allChecklists, posts]);

  const handlePrint = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') window.print();
  }, []);

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [pdfNotice, setPdfNotice] = useState(false);

  // Ref, not state: a second click can land before the loading re-render
  // makes the button inert, and two overlapping builds fired two downloads.
  const downloadInFlight = useRef(false);
  const handleDownloadPdf = useCallback(async () => {
    if (!report || downloadInFlight.current) return;
    downloadInFlight.current = true;
    setIsDownloadingPdf(true);
    try {
      const result = await downloadReportPdf(report);
      setPdfNotice(result.omittedFigureCount > 0 || result.imagesFailedCount > 0);
    } finally {
      setIsDownloadingPdf(false);
      downloadInFlight.current = false;
    }
  }, [report]);

  if (isLoading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Record" />
        <LoadingSpinner fullScreen message="Building the record..." />
      </View>
    );
  }

  if (!report || !structure) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Record" />
        <EmptyState
          icon={Printer}
          title="Nothing to record yet"
          message="This property needs a preparation model before a record can be produced."
        />
      </View>
    );
  }

  const place = [structure.city, structure.state].filter(Boolean).join(', ');

  return (
    <View ref={rootRef} style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Readiness Record - Redi</title>
      </Head>
      <View {...mark('printHide')}>
        <Header showBack title="Record" />
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent} {...mark('printFlow')}>
        <View style={styles.sheet}>
          <View style={styles.actions} {...mark('printHide')}>
            {isPdfDownloadSupported ? (
              <Button
                testID="report-download-pdf"
                title="Download PDF"
                onPress={handleDownloadPdf}
                leftIcon={Download}
                loading={isDownloadingPdf}
                size="sm"
              />
            ) : null}
            <Button
              title="Print or save as PDF"
              onPress={handlePrint}
              leftIcon={Printer}
              variant={isPdfDownloadSupported ? 'secondary' : 'primary'}
              size="sm"
            />
          </View>
          {pdfNotice ? (
            <Text
              style={[styles.meta, { color: colors.textSecondary, marginBottom: SPACING.md }]}
              {...mark('printHide')}>
              Some photographs could not be included in this download.
            </Text>
          ) : null}

          {/* --- Cover --- */}
          <View {...mark('printKeep')}>
            <Text style={[styles.overline, { color: colors.textSecondary }]}>
              Property readiness record
            </Text>
            <Text style={[styles.title, { color: colors.text }]}>
              {structure.address ?? 'Unknown address'}
            </Text>
            <Text style={[styles.meta, { color: colors.textSecondary }]}>
              {[place, `As of ${formatGeneratedAt()}`].filter(Boolean).join(' · ')}
            </Text>

            <View style={styles.coverRow}>
              <StaticPropertyMap
                latitude={structure.centroid.latitude}
                longitude={structure.centroid.longitude}
                size={300}
              />
              <View style={styles.headline}>
                {/* Two numbers, never one. Ticks alone would read as evidence
                    the record does not actually hold. */}
                <Text
                  testID="report-headline-documented"
                  style={[styles.bigNumber, { color: colors.text }]}>
                  {report.documentedCount} of {report.documentedTotal}
                </Text>
                <Text style={[styles.meta, { color: colors.textSecondary }]}>
                  items documented with an observation
                </Text>
                <Text
                  testID="report-headline-attested"
                  style={[styles.midNumber, { color: colors.textSecondary }]}>
                  {report.attestedCount} of {report.attestedTotal}
                </Text>
                <Text style={[styles.meta, { color: colors.textSecondary }]}>
                  marked complete on {report.modelName}
                </Text>
              </View>
            </View>
          </View>

          {/* --- Cross-model coverage: the page only REDI can produce --- */}
          <View style={[styles.block, { borderTopColor: colors.border }]} {...mark('printKeep')}>
            <Text style={[styles.h2, { color: colors.text }]}>How this reads across models</Text>
            <Text style={[styles.rowBody, { color: colors.textSecondary }]}>
              The same documented work, counted against each preparation model. Counted from
              observations on record, not from items ticked off.
            </Text>
            {report.coverage.map((c) => (
              <View
                key={c.checklistId}
                testID={`report-coverage-${c.checklistId}`}
                style={[styles.coverRowLine, { borderTopColor: colors.border }]}>
                <Text style={[styles.rowLabel, { color: colors.text }]}>
                  {c.name}
                  {c.isActive ? ' (current)' : ''}
                </Text>
                <Text style={[styles.rung, { color: colors.textSecondary }]}>
                  {c.documented} of {c.total}
                </Text>
              </View>
            ))}
          </View>

          {/* --- Zone sections --- */}
          {report.sections.map((section) => (
            <View
              key={section.key}
              testID={`report-section-${section.key}`}
              style={[styles.block, { borderTopColor: colors.border }]}
              {...mark('printBreak')}>
              <View style={styles.sectionHead}>
                <View style={styles.sectionTitle}>
                  <Text style={[styles.h2, { color: colors.text }]}>{section.label}</Text>
                  <Text style={[styles.meta, { color: colors.textSecondary }]}>
                    {section.detail}
                  </Text>
                </View>
                <Text style={[styles.rung, { color: colors.textSecondary }]}>
                  {section.documented} of {section.rows.length} documented
                </Text>
              </View>
              {section.rows.map((row) => (
                <Row key={row.key} row={row} />
              ))}
            </View>
          ))}

          {/* --- What is still open --- */}
          {report.openRows.length > 0 && (
            <View style={[styles.block, { borderTopColor: colors.border }]} {...mark('printBreak')}>
              <Text style={[styles.h2, { color: colors.text }]}>What is still open</Text>
              {report.openRows.map((row) => (
                <View
                  key={row.key}
                  style={[styles.coverRowLine, { borderTopColor: colors.border }]}>
                  <Text style={[styles.rowLabel, { color: colors.text }]}>{row.label}</Text>
                  <Text style={[styles.rung, { color: colors.textTertiary }]}>
                    {RUNG_LABEL[row.rung]}
                  </Text>
                </View>
              ))}
              <Text
                style={[styles.rowBody, { color: colors.textSecondary, marginTop: SPACING.sm }]}>
                Some conditions sit outside any one owner's control, such as road access, the width
                of the street, and what neighboring parcels do. They are not counted against this
                property.
              </Text>
            </View>
          )}

          {/* --- Timeline: this is a record, not a snapshot --- */}
          {report.timeline.length > 0 && (
            <View style={[styles.block, { borderTopColor: colors.border }]} {...mark('printBreak')}>
              <Text style={[styles.h2, { color: colors.text }]}>How this property has changed</Text>
              {report.timeline.map(({ post, label }) => (
                <View
                  key={post.id}
                  style={[styles.coverRowLine, { borderTopColor: colors.border }]}>
                  <Text style={[styles.rowLabel, { color: colors.text }]}>
                    {label ?? post.checklistItemLabel ?? 'Observation'}
                  </Text>
                  <Text style={[styles.rung, { color: colors.textSecondary }]}>
                    {formatShortDate(post.createdAt)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* --- Limits --- */}
          <View style={[styles.block, { borderTopColor: colors.border }]} {...mark('printKeep')}>
            <Text style={[styles.h2, { color: colors.text }]}>Limits of this record</Text>
            <Text style={[styles.rowBody, { color: colors.textSecondary }]}>
              This is a record of what has been observed and reported at this property, with the
              photographs and dates behind each item. Items marked complete without a photograph are
              the owner's own report. REDI does not inspect properties and does not verify the work,
              and this record does not certify compliance with any building code, fire code, or
              insurance requirement.
            </Text>
            <Text style={[styles.byline, { color: colors.textTertiary, marginTop: SPACING.sm }]}>
              Generated by REDI · weareredi.net · {formatGeneratedAt()}
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { alignItems: 'center', paddingBottom: SPACING.xxl },
  sheet: { width: '100%', maxWidth: CONTENT_MAX_WIDTH, padding: SPACING.md },
  actions: { alignItems: 'flex-start', paddingBottom: SPACING.md },
  overline: { ...TYPOGRAPHY.overline },
  title: { ...TYPOGRAPHY.screenTitle, marginTop: 2 },
  meta: { ...TYPOGRAPHY.metadata },
  h2: { ...TYPOGRAPHY.h4, marginBottom: SPACING.xs },
  coverRow: { flexDirection: 'row', gap: SPACING.lg, marginTop: SPACING.md, flexWrap: 'wrap' },
  headline: { flex: 1, minWidth: 200, justifyContent: 'center', gap: 2 },
  bigNumber: { ...TYPOGRAPHY.statNumeral },
  midNumber: { ...TYPOGRAPHY.h4, marginTop: SPACING.sm },
  block: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: SPACING.md,
    marginTop: SPACING.lg,
  },
  sectionHead: { flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm },
  sectionTitle: { flex: 1 },
  row: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: SPACING.smd,
    marginTop: SPACING.smd,
  },
  rowHead: { flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm },
  rowLabel: { ...TYPOGRAPHY.body, flex: 1 },
  rung: { ...TYPOGRAPHY.label },
  rowBody: { ...TYPOGRAPHY.bodySmall, marginTop: 2 },
  note: { marginTop: SPACING.sm, gap: 2 },
  byline: { ...TYPOGRAPHY.caption },
  coverRowLine: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: SPACING.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.sm,
  },
});
